import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { OrderType } from '../../shared/enums/order-type.enum';
import { AlertService } from './alert.service';
import { environment } from '../../../environments/environment';

declare var Razorpay: any;

interface CreateOrderResponse {
  rzpKey: string;
  currency: string;
  amount: number;
  orderId: string;
  rzpOrderId: string;
  _id:string
}

interface VerifyPaymentPayload {
  rzpOrderId: string;
  gatewayPaymentId: string;
  gatewaySignature: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  private baseUrl = `${environment.apiUrl}`;
  private pgTransactionId?: string;
  private pgReferenceId?: string;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private alertService: AlertService
  ) { }

  async createOrder(
    orderId: string,
    orderType: OrderType,
    amount: number,
    metadata: any
  ): Promise<CreateOrderResponse> {
    try {
      const body = { orderId, orderType, amount, metadata };
      const res: any = await firstValueFrom(
        this.http.post(`${this.baseUrl}/api/payments/create-order`, body)
      );

      return res?.order || res;
    } catch (error) {
      console.error('❌ Error creating order:', error);
      throw error;
    }
  }

  async verifyPayment(payload: VerifyPaymentPayload): Promise<any> {
    try {
      const res = await firstValueFrom(
        this.http.post(`${this.baseUrl}/api/payments/verify`, payload)
      );
      return res;
    } catch (error) {
      console.error('❌ Error verifying payment:', error);
      throw error;
    }
  }

  getPgDetails(): { pgTransactionId?: string; pgReferenceId?: string } {
    return {
      pgTransactionId: this.pgTransactionId,
      pgReferenceId: this.pgReferenceId,
    };
  }

  clearPgDetails(): void {
    this.pgTransactionId = undefined;
    this.pgReferenceId = undefined;
  }

  async startPayment(
    orderId: string,
    amount: number,
    customerName: string,
    customerContact: string,
    customerEmail: string,
    orderType: OrderType = OrderType.Reservation
  ): Promise<{ success: boolean, id?: string}> {
    if (!isPlatformBrowser(this.platformId)) return { success: false};

    return new Promise(async (resolve, reject) => {
      try {
        const order = await this.createOrder(orderId, orderType, amount, {
          customerName,
          customerContact,
          customerEmail
        });
        const createdOrderId = order._id;
        const options = {
          key: order.rzpKey,
          amount: order.amount,
          currency: order.currency || 'INR',
          name: 'Udaan Hotels & Resorts',
          description:
            orderType === OrderType.Takeaway
              ? 'Takeaway Order Payment'
              : orderType === OrderType.Hotel
                ? 'Hotel Reservation Payment'
                : 'Dine-in Reservation Payment',
          order_id: order.rzpOrderId,
          prefill: { name: customerName, contact: customerContact, email: customerEmail },
          theme: { color: '#109b9c' },
          handler: async (response: any) => {
            this.pgTransactionId = response?.razorpay_payment_id;
            this.pgReferenceId =  response?.razorpay_order_id;
            const verifyPayload = {
              rzpOrderId: response.razorpay_order_id,
              gatewayPaymentId: response.razorpay_payment_id,
              gatewaySignature: response.razorpay_signature,
            };

            try {
              await this.verifyPayment(verifyPayload);
              resolve({ success: true, id: createdOrderId });
            } catch (verifyError) {
              console.error('❌ Payment verification failed:', verifyError);
              resolve({ success: false, id: createdOrderId });
            }
          },
          modal: {
            ondismiss: () => {
              this.alertService.toast({ title: 'Payment cancelled.' });
              resolve({ success: false, id: createdOrderId });
            },
          },
        };

        const rzp = new Razorpay(options);
        rzp.open();
      } catch (error) {
        console.error('❌ Error starting payment:', error);
        reject(error);
      }
    });
  }
}
